import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, SkipBack, SkipForward } from "lucide-react";

const PlaybackTimeline = ({ 
  currentTime, 
  onTimeChange, 
  onStartTimeChange,
  onEndTimeChange,
  onTimeRangeChange,
  className = "" 
}) => {
  const [selectedTime, setSelectedTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeSections, setTimeSections] = useState([]);
  const [intervalMinutes, setIntervalMinutes] = useState(30);

  // Generate time sections based on selected interval for the selected date
  useEffect(() => {
    const sections = [];
    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    const now = new Date();
    
    // Calculate how many intervals fit in a day (24 hours * 60 minutes / interval)
    const intervalsPerDay = Math.floor(24 * 60 / intervalMinutes);
    
    // Generate all time sections for the selected date
    for (let i = 0; i < intervalsPerDay; i++) {
      const time = new Date(selectedDateStart.getTime() + (i * intervalMinutes * 60 * 1000));
      
      // Only show past times, not future times
      if (time < now) {
        const isCurrentInterval = time.toDateString() === now.toDateString() && 
                                 Math.abs(time.getTime() - now.getTime()) < intervalMinutes * 60 * 1000;
        
        sections.push({
          time,
          label: time.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          isCurrentInterval,
          isPast: time < now
        });
      }
    }
    
    console.log(`🎯 Timeline: Generated ${sections.length} sections for ${intervalMinutes}-min intervals`);
    console.log('🎯 Timeline: First few sections:', sections.slice(0, 5).map(s => s.label));
    console.log('🎯 Timeline: Last few sections:', sections.slice(-5).map(s => s.label));
    
    setTimeSections(sections);
  }, [selectedDate, intervalMinutes]);

  // Update selected time when currentTime prop changes
  useEffect(() => {
    if (currentTime) {
      setSelectedTime(new Date(currentTime));
    }
  }, [currentTime]);

  const handleTimeSelect = (time) => {
    console.log('🎯 Timeline: Time selected:', time);
    console.log('🎯 Timeline: Selected time string:', time.toLocaleTimeString());
    setSelectedTime(time);
    onTimeChange(time);
    
    // Update start and end times based on selection
    if (onStartTimeChange && onEndTimeChange) {
      const startTime = new Date(time);
      const endTime = new Date(time.getTime() + intervalMinutes * 60 * 1000); // Use selected interval
      
      // Format times for the time inputs
      const formatTimeForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      const startTimeStr = formatTimeForInput(startTime);
      const endTimeStr = formatTimeForInput(endTime);
      
      console.log('🎯 Timeline: Updating times - Start:', startTimeStr, 'End:', endTimeStr);
      
      onStartTimeChange(startTimeStr);
      onEndTimeChange(endTimeStr);
      
      // Trigger RTSP URL regeneration if callback is provided
      if (onTimeRangeChange) {
        console.log('🎯 Timeline: Calling onTimeRangeChange with:', startTimeStr, endTimeStr);
        onTimeRangeChange(startTimeStr, endTimeStr);
      } else {
        console.warn('⚠️ Timeline: onTimeRangeChange callback not provided');
      }
    } else {
      console.warn('⚠️ Timeline: onStartTimeChange or onEndTimeChange callbacks not provided');
    }
  };

  const navigateTime = (direction) => {
    const minutes = direction === 'forward' ? intervalMinutes : -intervalMinutes;
    const newTime = new Date(selectedTime.getTime() + (minutes * 60 * 1000));
    setSelectedTime(newTime);
    onTimeChange(newTime);
    handleTimeSelect(newTime); // Also update start/end times
  };

  const jumpToTime = (direction) => {
    const hours = direction === 'forward' ? 1 : -1;
    const newTime = new Date(selectedTime.getTime() + (hours * 60 * 60 * 1000));
    setSelectedTime(newTime);
    onTimeChange(newTime);
    handleTimeSelect(newTime); // Also update start/end times
  };

  const handleDateChange = (date) => {
    setSelectedDate(new Date(date));
    // Reset selected time to start of the new date
    const newTime = new Date(date);
    newTime.setHours(0, 0, 0, 0);
    setSelectedTime(newTime);
    onTimeChange(newTime);
    handleTimeSelect(newTime);
  };

  const handleIntervalChange = (interval) => {
    setIntervalMinutes(parseInt(interval));
  };

  const formatSelectedTime = (time) => {
    return time.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 p-4 ${className}`}>
      {/* Timeline Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">Playback Timeline</h3>
          <span className="text-sm text-gray-500">• {intervalMinutes}-min intervals</span>
        </div>
        
        {/* Controls */}
        <div className="flex items-center space-x-3">
          {/* Interval Selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="interval-selector" className="text-sm font-medium text-gray-700">
              Interval:
            </label>
            <select
              id="interval-selector"
              value={intervalMinutes}
              onChange={(e) => handleIntervalChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={1}>1 min</option>
              <option value={5}>5 min</option>
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
            </select>
          </div>

          {/* Date Picker */}
          <div className="flex items-center space-x-2">
            <label htmlFor="date-picker" className="text-sm font-medium text-gray-700">
              Date:
            </label>
            <input
              id="date-picker"
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => handleDateChange(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          {/* Selected Time Display */}
          <div className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
            {formatSelectedTime(selectedTime)}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => jumpToTime('backward')}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Jump back 1 hour"
          >
            <SkipBack className="h-5 w-5" />
          </button>
          
          <button
            onClick={() => navigateTime('backward')}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title={`Go back ${intervalMinutes} minutes`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            onClick={() => navigateTime('forward')}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title={`Go forward ${intervalMinutes} minutes`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          
          <button
            onClick={() => jumpToTime('forward')}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Jump forward 1 hour"
          >
            <SkipForward className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Timeline Grid */}
      <div className="overflow-x-auto">
        <div className="flex space-x-1 min-w-max">
          {timeSections.map((section, index) => {
            const isSelected = selectedTime && 
              Math.abs(selectedTime.getTime() - section.time.getTime()) < 60 * 1000; // Within 1 minute for precise selection
            
            return (
              <button
                key={index}
                onClick={() => handleTimeSelect(section.time)}
                className={`
                  flex-shrink-0 rounded-lg border-2 transition-all duration-200
                  ${intervalMinutes <= 5 ? 'w-12 h-10' : intervalMinutes <= 15 ? 'w-14 h-11' : 'w-16 h-12'}
                  ${isSelected 
                    ? 'border-indigo-500 bg-indigo-100 text-indigo-700' 
                    : section.isCurrentInterval
                    ? 'border-green-400 bg-green-50 text-green-700 hover:border-green-500'
                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                  }
                `}
                title={`${formatSelectedTime(section.time)}${section.isCurrentInterval ? ' (Current)' : ''}`}
              >
                <div className={`font-medium ${intervalMinutes <= 5 ? 'text-xs' : 'text-xs'}`}>
                  {section.label}
                </div>
                {section.isCurrentInterval && (
                  <div className="text-xs text-green-600 font-bold">NOW</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default PlaybackTimeline;
